/**
 * Labels for App Analytics dashboard charts.
 * Keep keys in sync with: microlab/src/analytics/screenCatalog.ts
 */

export const SCREEN_ROUTE_CATALOG = {
  Slides: { title: "Slides tab", description: "Main tab: histology / embryology catalogs, 3D models, games, presentations." },
  Quiz: { title: "Quiz tab", description: "University quizzes, rankings, and quiz results." },
  UsmleHub: { title: "USMLE Prep home", description: "Choose a USMLE step, track completed sets, and start practice." },
  UsmleSetup: { title: "USMLE setup", description: "Select USMLE step and subjects, then start a 10-question attempt." },
  UsmlePlay: { title: "USMLE attempt", description: "Answer 10 USMLE-style multiple-choice questions." },
  UsmleResults: { title: "USMLE results", description: "Score, correct answers, and saved teaching explanations after an attempt." },
  Community: { title: "Community tab", description: "Community feed, reels, and social content." },
  Questions: { title: "Questions tab", description: "Ask questions, browse Q&A, and AI-assisted posts." },
  Notification: { title: "Notifications tab", description: "In-app notification inbox and related compose flows." },
  Favorite: { title: "Favorites tab", description: "Saved slides and favorite content." },
  SlideScreen: { title: "Slide library home", description: "Browse histology, embryology, 3D, games, and Microlab Copilot." },
  LectureLabProcessing: { title: "Lecture Lab — processing", description: "Upload progress and AI pipeline while building a study pack." },
  StudyPack: { title: "Lecture Lab — study pack", description: "Generated summary, quiz, and flashcards from uploaded material." },
  Tile: { title: "Slide viewer", description: "Full-screen interactive slide / tile viewer." },
  Show3DTiles: { title: "3D model catalog", description: "Grid of 3D histology or embryology models." },
  ThreeDModelScreen: { title: "3D model viewer", description: "Single 3D model viewer." },
  PresentationDetails: { title: "Presentation details", description: "University presentation metadata and open/view flow." },
  HistologyGameFlow: { title: "Histology game", description: "Gamified histology practice flow." },
  inAppPurchase: { title: "Subscriptions & paywall", description: "Plans, checkout, and subscription management." },
  SuccessfulPurchase: { title: "Purchase success", description: "Confirmation after a successful purchase." },
  ProfileScreen: { title: "Profile", description: "User profile overview and settings entry." },
  Profile: { title: "Profile (stack)", description: "Profile stack root." },
  EditProfile: { title: "Edit profile", description: "Change name, photo, occupation, and profile fields." },
  EditProfileScreen: { title: "Edit profile", description: "Alternate route for profile editing." },
  SubscriptionScreen: { title: "Subscription details", description: "Current plan and renewal info." },
  SupportScreen: { title: "Support", description: "Help and support options." },
  EULA: { title: "Terms / EULA", description: "Legal terms and license display." },
  FavoriteScreen: { title: "Favorites list", description: "List of saved slides." },
  ChangePasswordScreen: { title: "Change password", description: "Update account password." },
  AboutScreen: { title: "About", description: "App version and about information." },
  EarnMoneyScreen: { title: "Earnings / referrals", description: "Referral or earnings dashboard." },
  EarnMoneyTermsScreen: { title: "Earnings terms", description: "Terms for referral or earnings program." },
  AskQuestionScreen: { title: "Compose post / question", description: "Create a community question or post (text, image, video)." },
  AskAIScreen: { title: "Microlab AI chat", description: "AI assistant for study help." },
  QuestionDetailsScreen: { title: "Question / post thread", description: "Single thread with answers, comments, and reactions." },
  EditQuestionScreen: { title: "Edit post", description: "Edit an existing community post." },
  ImageCanvasScreen: { title: "Image markup", description: "Draw or annotate on an image for a post." },
  PublicProfileScreen: { title: "Public profile", description: "Another user’s public profile." },
  BioLinkWebView: { title: "Bio link browser", description: "In-app browser for profile link-out." },
  CommunityScreen: { title: "Community feed", description: "Main community timeline." },
  CommunityReelsScreen: { title: "Community reels", description: "Short-form reel-style community feed." },
  NotificationScreen: { title: "Notification list", description: "List of in-app notifications." },
  QuizList: { title: "Quiz list", description: "Available quizzes and join by code." },
  QuizScreen: { title: "Active quiz", description: "Taking a timed quiz." },
  QuizRulesScreen: { title: "Quiz rules", description: "Rules before starting a quiz." },
  StartQuizScreen: { title: "Quiz start", description: "Countdown or start confirmation." },
  ResultQuizScreen: { title: "Quiz result", description: "Score summary after a quiz attempt." },
  RankingQuizScreen: { title: "Quiz ranking", description: "Leaderboard for a quiz." },
  AnalyticQuizScreen: { title: "Quiz analytics", description: "Per-question or performance analytics for a quiz." },
  AllResultsScreen: { title: "All quiz results", description: "History of quiz attempts." },
  RequestDemoScreen: { title: "Request demo", description: "Educator demo request flow." },
  Landing: { title: "Welcome / landing", description: "Pre-login marketing or welcome." },
  LoginScreen: { title: "Login", description: "Sign in with email or social." },
  Login: { title: "Login", description: "Login stack screen." },
  Register: { title: "Register", description: "Create account." },
  Register2: { title: "Register (step 2)", description: "Additional registration step." },
  AppleRegisterProfile: { title: "Complete Apple profile", description: "Finish profile after Sign in with Apple." },
  TermsScreen: { title: "Terms", description: "Terms of service." },
  EULAScreen: { title: "EULA", description: "End user license agreement." },
  Forgot: { title: "Forgot password", description: "Password recovery start." },
  Forgot2: { title: "Forgot password (confirm)", description: "Password recovery follow-up." },
  Support: { title: "Support (auth)", description: "Support from logged-out flow." },
  notification: {
    title: "Push notification (system)",
    description: "User opened the app from a push notification; see notification type on the event.",
  },
};

export const PUSH_TYPE_LABELS = {
  subscription_reminder: "Subscription reminder (paywall)",
  news: "News / external link",
  earnings_update: "Earnings / referrals update",
  new_question: "New community question",
  question_seek_comment: "Comment / engagement on your post",
  view_milestone: "View-count milestone",
  reaction_milestone: "Reaction milestone",
  community_post_discovery: "Community post discovery",
  mention: "Mention in thread",
  comment: "New comment",
  unknown: "Unknown / generic notification",
};

export function formatScreenChartLabel(screenName) {
  const key = String(screenName || "").trim();
  const entry = SCREEN_ROUTE_CATALOG[key];
  if (entry) return `${entry.title} [${key}]`;
  return key || "Unknown";
}

export function getPushTypeHumanLabel(notificationType) {
  const key = String(notificationType || "unknown").trim() || "unknown";
  return PUSH_TYPE_LABELS[key] || PUSH_TYPE_LABELS.unknown;
}

export function formatPushTypeChartLabel(notificationType) {
  const key = String(notificationType || "unknown").trim() || "unknown";
  const human = getPushTypeHumanLabel(key);
  return `${human} (${key})`;
}

export function parseEventMetadata(row) {
  const raw = row?.metadata;
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}
